import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  User,
  Phone,
  MapPin,
  Briefcase,
  FileText,
  Loader2,
  Mail,
  Info,
  Award,
  XCircle,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { CustomFieldInlineEditor } from '../ui/CustomFieldInlineEditor';

export const SpecialistFormPanel = ({ 
  specialist, 
  isOpen, 
  onClose, 
  onSubmit,
  specialistTypes = [],
  cities = []
}) => {
  const { t } = useTranslation('specialists');
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [newCity, setNewCity] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    document_type: 'CC',
    document_number: '',
    email: '',
    phone: '',
    specialist_type_id: '',
    license_number: '',
    service_cities: [],
    specialties: [],
    hourly_rate: 0,
    status: 'active',
    notes: '',
    custom_field_values: []
  });

  // Reset form when specialist changes
  useEffect(() => {
    if (isOpen) {
      if (specialist) {
        // Normalize specialties to strings if they come as objects
        const normalizedSpecialties = (specialist.specialties || []).map(spec => 
          typeof spec === 'object' ? spec.name : spec
        ).filter(Boolean);
        
        setFormData({
          first_name: specialist.first_name || '',
          last_name: specialist.last_name || '',
          document_type: specialist.document_type || 'CC',
          document_number: specialist.document_number || '',
          email: specialist.email || '',
          phone: specialist.phone || '',
          specialist_type_id: specialist.specialist_type_id || '',
          license_number: specialist.license_number || '',
          service_cities: specialist.service_cities || [],
          specialties: normalizedSpecialties,
          hourly_rate: specialist.hourly_rate || 0,
          status: specialist.status || 'active',
          notes: specialist.notes || '',
          custom_field_values: specialist.custom_field_values || []
        });
      } else {
        setFormData({
          first_name: '',
          last_name: '',
          document_type: 'CC',
          document_number: '',
          email: '',
          phone: '',
          specialist_type_id: '',
          license_number: '',
          service_cities: [],
          specialties: [],
          hourly_rate: 0,
          status: 'active',
          notes: '',
          custom_field_values: []
        });
      }
      setActiveTab('basic');
    }
  }, [specialist, isOpen]);

  const handleAddCity = () => {
    const trimmed = newCity.trim();
    if (trimmed && !formData.service_cities.includes(trimmed)) {
      setFormData({
        ...formData,
        service_cities: [...formData.service_cities, trimmed]
      });
      setNewCity('');
    }
  };

  const handleRemoveCity = (city) => {
    setFormData({
      ...formData,
      service_cities: formData.service_cities.filter(c => c !== city)
    });
  };

  const handleAddSpecialty = () => {
    const trimmed = newSpecialty.trim();
    if (trimmed && !formData.specialties.includes(trimmed)) {
      setFormData({
        ...formData,
        specialties: [...formData.specialties, trimmed]
      });
      setNewSpecialty('');
    }
  };

  const handleRemoveSpecialty = (specialty) => {
    setFormData({
      ...formData,
      specialties: formData.specialties.filter(s => s !== specialty)
    });
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.document_number) {
      toast.error(t('form.requiredFields'));
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving specialist:', error);
      toast.error(t('messages.errorCreating'));
    }
    setLoading(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl md:max-w-2xl overflow-hidden flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <Briefcase className="h-6 w-6 text-[#E63946]" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {specialist?.id ? t('form.editTitle') : t('form.title')}
              </SheetTitle>
              <SheetDescription>
                {specialist?.id ? t('form.editSubtitle') : t('form.subtitle')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="basic" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Info className="h-4 w-4 mr-1" />
              {t('form.tabs.basic')}
            </TabsTrigger>
            <TabsTrigger value="professional" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Award className="h-4 w-4 mr-1" />
              {t('form.tabs.professional')}
            </TabsTrigger>
            <TabsTrigger value="coverage" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <MapPin className="h-4 w-4 mr-1" />
              {t('form.tabs.coverage')}
            </TabsTrigger>
            <TabsTrigger value="custom" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Plus className="h-4 w-4 mr-1" />
              {t('form.tabs.custom')}
              {formData.custom_field_values?.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">
                  {formData.custom_field_values.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            {/* Basic Info Tab */}
            <TabsContent value="basic" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.firstName')} *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder={t('form.firstNamePlaceholder')}
                    data-testid="specialist-first-name"
                  />
                </div>
                <div>
                  <Label>{t('form.lastName')} *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder={t('form.lastNamePlaceholder')}
                    data-testid="specialist-last-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.documentType')} *</Label>
                  <Select
                    value={formData.document_type}
                    onValueChange={(v) => setFormData({...formData, document_type: v})}
                  >
                    <SelectTrigger data-testid="specialist-doc-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CC">{t('documentTypes.CC')}</SelectItem>
                      <SelectItem value="CE">{t('documentTypes.CE')}</SelectItem>
                      <SelectItem value="PA">{t('documentTypes.PA')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('form.documentNumber')} *</Label>
                  <Input
                    value={formData.document_number}
                    onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                    placeholder={t('form.documentNumberPlaceholder')}
                    data-testid="specialist-doc-number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder={t('form.emailPlaceholder')}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('form.phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder={t('form.phonePlaceholder')}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>{t('form.status')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('status.active')}</SelectItem>
                    <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
                    <SelectItem value="on_leave">{t('status.on_leave')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            {/* Professional Tab */}
            <TabsContent value="professional" className="p-6 space-y-4 mt-0">
              <div>
                <Label>{t('form.specialistType')}</Label>
                <Select
                  value={formData.specialist_type_id}
                  onValueChange={(v) => setFormData({...formData, specialist_type_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('form.selectSpecialistType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {specialistTypes.filter(st => st.status === 'active').map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('form.licenseNumber')}</Label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                  placeholder={t('form.licenseNumberPlaceholder')}
                />
              </div>

              <div>
                <Label>{t('form.hourlyRate')}</Label>
                <Input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                />
              </div>

              <div className="border rounded-lg p-4">
                <Label className="mb-2 block">{t('form.specialties')}</Label>
                
                {formData.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.specialties.map((specialty, idx) => {
                      // Handle both string and object specialties
                      const specialtyName = typeof specialty === 'object' ? specialty.name : specialty;
                      return (
                        <Badge key={idx} variant="secondary" className="pr-1">
                          {specialtyName}
                          <button
                            type="button"
                            onClick={() => handleRemoveSpecialty(specialty)}
                            className="ml-1 hover:text-red-600 p-0.5"
                          >
                            <XCircle className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Input
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSpecialty())}
                    placeholder={t('form.specialtyPlaceholder')}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={handleAddSpecialty} disabled={!newSpecialty.trim()}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label>{t('form.notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder={t('form.notesPlaceholder')}
                  rows={3}
                />
              </div>
            </TabsContent>

            {/* Coverage Tab */}
            <TabsContent value="coverage" className="p-6 space-y-4 mt-0">
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <Label className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  {t('form.serviceCities')}
                </Label>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('form.serviceCitiesHelp')}
                </p>
                
                {formData.service_cities.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.service_cities.map((city, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-blue-100 text-blue-800 pr-1">
                        {city}
                        <button
                          type="button"
                          onClick={() => handleRemoveCity(city)}
                          className="ml-1 hover:text-red-600 p-0.5"
                        >
                          <XCircle className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Select
                    value=""
                    onValueChange={(v) => {
                      const city = cities.find(c => c.id === v);
                      if (city && !formData.service_cities.includes(city.name)) {
                        setFormData({
                          ...formData,
                          service_cities: [...formData.service_cities, city.name]
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={t('form.selectCity')} />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.filter(c => c.status === 'active').map(city => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-3 flex gap-2">
                  <Input
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCity())}
                    placeholder={t('form.cityManualPlaceholder')}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={handleAddCity} disabled={!newCity.trim()}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {formData.service_cities.length === 0 && (
                <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                  <MapPin className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>{t('form.noCitiesConfigured')}</p>
                </div>
              )}
            </TabsContent>

            {/* Custom Fields Tab */}
            <TabsContent value="custom" className="p-6 mt-0">
              <CustomFieldInlineEditor
                entityType="specialist"
                customFieldValues={formData.custom_field_values || []}
                onValuesChange={(values) => setFormData({ ...formData, custom_field_values: values })}
              />
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common:cancel')}</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[#E63946] hover:bg-[#d32836]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {specialist?.id ? t('form.update') : t('form.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SpecialistFormPanel;
